import React, { useRef, useState } from 'react';
import { LuCamera, LuImage, LuImages, LuX, LuTag } from 'react-icons/lu';
import { construirUrlFoto } from '../../utils/construirUrlFoto';
import { comprimirFoto, fileADataUrl } from './FotoUpload';
import { ROTULOS_RAPIDOS } from '../../utils/rotulosFotos';

// Fotos de un equipo (14-oct-2026): hasta 4 por equipo, las mismas casillas en todos los cierres y
// en la ficha del trabajo. Los valores pueden ser data URLs (recién sacadas) o nombres de archivo ya
// subidos; quien guarda sube las nuevas con subirFotosEquipo (utils/subirFoto).
// 18-oct-2026: ya no hay "antes / después" fijo ni obligatorio. Cada foto lleva un rótulo corto
// ("Antes · frente", "Falla", "tapa frontal rota"…), elegido con un toque o escrito, que sale en el PDF.
// Por dentro siguen las casillas fotoAntes, fotoDespues y fotosExtra (es donde se guardan).
export const MAX_EXTRA = 2;
const ver = f => (String(f).startsWith('data:') ? f : construirUrlFoto(f));

// Ninguna foto es obligatoria para cerrar (18-oct-2026): si el cliente no mandó la de antes, no hay que
// subir la de otro equipo. Se deja la función para no tocar las pantallas que la usan.
export const faltanFotos = () => [];

async function leer(files) {
    const out = [];
    for (const f of files) {
        const d = await fileADataUrl(await comprimirFoto(f)); // eslint-disable-line no-await-in-loop
        if (d) out.push(d);
    }
    return out;
}

function EditorRotulo({ valor, onListo }) {
    const [texto, setTexto] = useState(valor || '');
    return (
        <div className="col-span-4 p-2.5 rounded-xl bg-panel space-y-2">
            <div className="flex flex-wrap gap-1.5">
                {ROTULOS_RAPIDOS.map(r => (
                    <button key={r} type="button" onClick={() => onListo(r)}
                        className={`h-8 px-2.5 rounded-lg text-label font-bold active:scale-95 ${valor === r ? 'bg-brand-red text-white' : 'bg-chip text-ink'}`}>{r}</button>
                ))}
            </div>
            <div className="flex gap-1.5">
                <input value={texto} onChange={e => setTexto(e.target.value)} maxLength={40} autoFocus
                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); onListo(texto); } }}
                    placeholder="O escribí: tapa frontal rota…" className="flex-1 min-w-0 h-9 px-3 rounded-lg bg-chip text-ink text-caption outline-none placeholder:text-muted" />
                <button type="button" onClick={() => onListo(texto)} className="h-9 px-3 rounded-lg bg-ink text-page text-label font-black">Listo</button>
            </div>
        </div>
    );
}

function Casilla({ numero, foto, rotulo, editando, onRotulo, onCamara, onGaleria, onSacar }) {
    const nombre = `Foto ${numero}`;
    return (
        <div className="min-w-0">
            {foto ? (
                <>
                    <div className="relative aspect-[3/4] rounded-xl overflow-hidden bg-chip">
                        <img src={ver(foto)} alt={rotulo || nombre} className="w-full h-full object-cover" />
                        <button type="button" onClick={onSacar} aria-label={`Sacar ${nombre}`}
                            className="absolute top-1 right-1 w-7 h-7 rounded-full bg-black/60 text-white flex items-center justify-center active:scale-90">
                            <LuX size={14} />
                        </button>
                    </div>
                    <button type="button" onClick={onRotulo}
                        className={`mt-1 w-full min-h-[28px] px-1.5 rounded-lg text-[11px] leading-tight font-bold flex items-center justify-center gap-1 text-center ${editando ? 'bg-brand-red text-white' : rotulo ? 'bg-chip text-ink' : 'border border-dashed border-black/20 dark:border-white/20 text-muted'}`}>
                        {rotulo ? <span className="line-clamp-2">{rotulo}</span> : <><LuTag size={11} /> ¿Qué es?</>}
                    </button>
                </>
            ) : (
                <>
                    <div className="aspect-[3/4] rounded-xl border border-dashed grid grid-rows-2 overflow-hidden border-black/15 dark:border-white/15">
                        <button type="button" onClick={onCamara} aria-label={`${nombre}: sacar foto`}
                            className="flex flex-col items-center justify-center gap-0.5 text-label font-black text-brand-red border-b border-dashed border-black/10 dark:border-white/10 active:bg-chip">
                            <LuCamera size={16} /> Cámara
                        </button>
                        <button type="button" onClick={onGaleria} aria-label={`${nombre}: elegir de la galería`}
                            className="flex flex-col items-center justify-center gap-0.5 text-label font-black text-secondary active:bg-chip">
                            <LuImage size={16} /> Galería
                        </button>
                    </div>
                    <p className="mt-1 text-center text-[11px] font-bold text-muted">{nombre}</p>
                </>
            )}
        </div>
    );
}

/**
 * value: { fotoAntes, fotoDespues, fotosExtra, fotosRotulos }   onChange(parcial) con los campos que cambian.
 * fotosRotulos: { antes, despues, extra: [] } — el rótulo de cada casilla.
 */
export default function FotosEquipo({ fotoAntes = null, fotoDespues = null, fotosExtra = [], fotosRotulos = null, onChange, disabled = false }) {
    const camara = useRef(null);
    const galeria = useRef(null);
    const varias = useRef(null);
    const [destino, setDestino] = useState(null); // 'antes' | 'despues' | 'extra'
    const [editando, setEditando] = useState(null); // clave de la casilla cuyo rótulo se edita
    const extra = (fotosExtra || []).slice(0, MAX_EXTRA);
    const rot = { antes: fotosRotulos?.antes || null, despues: fotosRotulos?.despues || null, extra: [...(fotosRotulos?.extra || [])] };
    const libres = (!fotoAntes ? 1 : 0) + (!fotoDespues ? 1 : 0) + (MAX_EXTRA - extra.length);
    const conRotulos = (r) => ({ fotosRotulos: { antes: r.antes || null, despues: r.despues || null, extra: r.extra.slice(0, MAX_EXTRA) } });

    const poner = (fotos, donde) => {
        if (!fotos.length) return;
        if (donde === 'antes') return onChange({ fotoAntes: fotos[0] });
        if (donde === 'despues') return onChange({ fotoDespues: fotos[0] });
        if (donde === 'extra') return onChange({ fotosExtra: [...extra, ...fotos].slice(0, MAX_EXTRA) });
        // Varias de la galería: llenan los lugares vacíos en orden
        const cola = [...fotos];
        const cambios = {};
        if (!fotoAntes && cola.length) cambios.fotoAntes = cola.shift();
        if (!fotoDespues && cola.length) cambios.fotoDespues = cola.shift();
        if (cola.length) cambios.fotosExtra = [...extra, ...cola].slice(0, MAX_EXTRA);
        onChange(cambios);
    };
    const elegido = async (e, donde) => {
        const files = [...(e.target.files || [])];
        e.target.value = '';
        poner(await leer(donde ? files.slice(0, donde === 'extra' ? MAX_EXTRA - extra.length : 1) : files.slice(0, libres)), donde);
    };
    const abrir = (ref, donde) => { setDestino(donde); ref.current?.click(); };
    const ponerRotulo = (k, texto) => {
        const t = String(texto || '').trim().slice(0, 40) || null;
        const r = { ...rot, extra: [...rot.extra] };
        if (k === 'antes' || k === 'despues') r[k] = t;
        else r.extra[Number(k.slice(5))] = t;
        onChange(conRotulos(r));
        setEditando(null);
    };

    const casillas = [
        { k: 'antes', foto: fotoAntes, rotulo: rot.antes,
          sacar: () => onChange({ fotoAntes: null, ...conRotulos({ ...rot, antes: null }) }) },
        { k: 'despues', foto: fotoDespues, rotulo: rot.despues,
          sacar: () => onChange({ fotoDespues: null, ...conRotulos({ ...rot, despues: null }) }) },
        ...[0, 1].map(i => ({
            k: `extra${i}`, foto: extra[i] || null, rotulo: rot.extra[i] || null,
            sacar: () => onChange({ fotosExtra: extra.filter((_, j) => j !== i), ...conRotulos({ ...rot, extra: rot.extra.filter((_, j) => j !== i) }) }),
            // Las extras se llenan en orden: la 4 recién se habilita con la 3 cargada
            oculta: i > extra.length,
        })),
    ];
    const enEdicion = casillas.find(c => c.k === editando);

    return (
        <div className={disabled ? 'opacity-60 pointer-events-none' : ''}>
            <div className="grid grid-cols-4 gap-1.5">
                {casillas.map((c, i) => c.oculta ? <div key={c.k} /> : (
                    <Casilla key={c.k} numero={i + 1} foto={c.foto} rotulo={c.rotulo} editando={editando === c.k}
                        onRotulo={() => setEditando(e => (e === c.k ? null : c.k))}
                        onCamara={() => abrir(camara, c.k.startsWith('extra') ? 'extra' : c.k)}
                        onGaleria={() => abrir(galeria, c.k.startsWith('extra') ? 'extra' : c.k)}
                        onSacar={() => { if (editando === c.k) setEditando(null); c.sacar(); }} />
                ))}
                {enEdicion && <EditorRotulo key={enEdicion.k} valor={enEdicion.rotulo} onListo={t => ponerRotulo(enEdicion.k, t)} />}
            </div>
            {libres > 1 && (
                <button type="button" onClick={() => varias.current?.click()}
                    className="mt-1.5 h-9 w-full rounded-lg bg-chip text-secondary text-label font-bold flex items-center justify-center gap-1.5 active:scale-[0.98]">
                    <LuImages size={14} /> Elegir varias de la galería
                </button>
            )}
            <input ref={camara} type="file" accept="image/*" capture="environment" className="hidden" onChange={e => elegido(e, destino)} />
            <input ref={galeria} type="file" accept="image/*" multiple={destino === 'extra'} className="hidden" onChange={e => elegido(e, destino)} />
            <input ref={varias} type="file" accept="image/*" multiple className="hidden" onChange={e => elegido(e, null)} />
        </div>
    );
}

